/**
 * TOSS Web Application - AppLayout Component
 *
 * Main application layout that provides responsive structure.
 * Automatically switches between three layout modes based on viewport.
 *
 * Layout structure:
 *
 * Desktop (>= 1024px):
 * ┌──────────────┬────────────────────────────────────┐
 * │              │            Header                  │
 * │   Sidebar    ├────────────────────────────────────┤
 * │   (280px)    │            Content                 │
 * │   expanded   │                                    │
 * └──────────────┴────────────────────────────────────┘
 *
 * Tablet (768px - 1023px):
 * ┌────┬─────────────────────────────────────────────┐
 * │    │            Header                           │
 * │64px├─────────────────────────────────────────────┤
 * │icon│            Content                          │
 * │only│                                             │
 * └────┴─────────────────────────────────────────────┘
 *
 * Mobile (< 768px):
 * ┌──────────────────────────────────────────────────┐
 * │                    Header                         │
 * ├──────────────────────────────────────────────────┤
 * │                                                   │
 * │                    Content                        │
 * │                   (100% width)                    │
 * │                                                   │
 * ├──────────────────────────────────────────────────┤
 * │                   NavBar                          │
 * └──────────────────────────────────────────────────┘
 *
 * Features:
 * - Three responsive layout modes (mobile, tablet, desktop)
 * - Desktop: Full sidebar (280px) with labels
 * - Tablet: Collapsed sidebar (64px) with icons only
 * - Mobile: Header + Full-width content + Bottom NavBar
 * - Mobile drawer menu (slide-out sidebar)
 * - Different background colors per device type
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useResponsive } from '@/hooks/useResponsive';
import { useUIStore, useMobileMenu } from '@/store/uiStore';
import { useMessengerSignalR } from '@/features/messenger/hooks/useMessengerSignalR';
import { Header } from './Header';
import { Sidebar, navigationItems } from './Sidebar';
import { CalculatorPanel } from './CalculatorPanel';
import { MessengerFab } from './MessengerFab';
import { CommandPalette } from './CommandPalette';
import { pushRecentPath } from './commandPaletteRecent';
import { RootBackArrowContext } from '@/components/ui/rootBackArrow';

// Leaf nav paths (children of groups + leaf top-level items) — used to record
// "recent pages" for the command palette only when a real nav page is visited.
const NAV_LEAF_PATHS: ReadonlySet<string> = (() => {
  const set = new Set<string>();
  for (const item of navigationItems) {
    if (item.children && item.children.length > 0) {
      item.children.forEach((c) => set.add(c.path));
    } else {
      set.add(item.path);
    }
  }
  return set;
})();

// ============================================================================
// Types
// ============================================================================

/**
 * Content and shell switches only.
 *
 * `pageTitle`, `headerActions` and `showSearch` were removed on 2026-08-26 with the top-bar
 * unification: no caller had ever passed any of them, so the title slot in `Header` printed an
 * empty string on every screen of the application while every page named itself through
 * `PageHeader`. A prop that merely looks wired is worse than no prop — it is where a future
 * screen would put its title back, and a bar that prints one is decision Р33 all over again.
 */
export interface AppLayoutProps {
  /** Page content */
  children: ReactNode;
  /** Hide sidebar on desktop */
  hideSidebar?: boolean;
  /** Hide header */
  hideHeader?: boolean;
  /** Hide bottom navigation on mobile */
  hideNavBar?: boolean;
  /** Additional class names for main content area */
  contentClassName?: string;
  /** Use fixed viewport height (for chat-like pages that need viewport-locked layout) */
  fullHeight?: boolean;
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Mobile drawer sidebar with enter/exit animations
 */
function MobileDrawer({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}): JSX.Element | null {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const previousScrollY = useRef(0);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsClosing(false);
      // Lock body scroll — position:fixed approach for iOS
      const scrollY = window.scrollY;
      previousScrollY.current = scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    } else if (isVisible) {
      // Start closing animation
      setIsClosing(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsClosing(false);
      }, 250); // Match animation duration
      // Restore body scroll
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, previousScrollY.current);
      return () => clearTimeout(timer);
    }

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
    };
  }, [isOpen, isVisible]);

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] transition-opacity duration-250 ${
          isClosing ? 'opacity-0' : 'opacity-100'
        }`}
        onClick={onClose}
        onTouchMove={(e) => e.preventDefault()}
        aria-hidden="true"
      />
      {/* Drawer.
          Ящик — это тот же `Sidebar`, только выехавший, поэтому и подложка у него цвета рейла:
          она закрывает затемнение под собой, пока панель едет, и не даёт краю мигать серым.
          `variant="flush"` — потому что здесь рейл занимает край экрана целиком: отступ и
          скругление, уместные на канве компьютера, внутри выехавшей панели читались бы как
          вторая рамка. */}
      <div
        className={`fixed inset-y-0 left-0 z-[9999] w-sidebar max-w-[85vw] pt-[env(safe-area-inset-top,0px)] bg-[var(--sidebar-bg)] ${
          isClosing ? 'animate-slide-out-left' : 'animate-slide-in-left'
        }`}
      >
        <Sidebar onNavigate={onClose} variant="flush" />
      </div>
    </>
  );
}

// ============================================================================
// Skip Link (WCAG 2.4.1 Bypass Blocks)
// ============================================================================

/**
 * Visually-hidden link that becomes visible on keyboard focus, letting keyboard
 * and screen-reader users jump straight to <main> past the sidebar/header.
 * Targets the `#main-content` id set on the <main> element in each layout.
 */
function SkipLink(): JSX.Element {
  return (
    <a
      href="#main-content"
      className="
        sr-only focus:not-sr-only
        focus:fixed focus:top-3 focus:left-3 focus:z-[10000]
        focus:px-4 focus:py-2 focus:rounded-md
        text-md
        focus:bg-primary-500 focus:text-white focus:shadow-[var(--shadow-overlay)]
        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
      "
    >
      Перейти к основному содержимому
    </a>
  );
}

// ============================================================================
// Desktop/Tablet Layout
// ============================================================================

/**
 * Desktop and Tablet layout with sidebar
 * Desktop (>= 1024px): Full sidebar (280px)
 * Tablet (768px - 1023px): Collapsed sidebar (64px)
 */
function DesktopTabletLayout({
  children,
  hideSidebar,
  hideHeader,
  contentClassName,
  fullHeight,
  sidebarCollapsed,
  onToggleSidebar,
}: Omit<AppLayoutProps, 'hideNavBar'> & {
  sidebarCollapsed: boolean;
  onToggleSidebar?: () => void;
}): JSX.Element {
  return (
    <div className="h-dvh overflow-hidden bg-[var(--color-bg-app)] flex">
      <SkipLink />
      {/* Sidebar - на всю высоту слева, содержит логотип вверху (стиль flowers) */}
      {!hideSidebar && (
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggleCollapsed={onToggleSidebar}
        />
      )}

      {/* Колонка контента: шапка + содержимое (справа от сайдбара) */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header — карточка на канве, как боковое меню и как содержимое.

            Отступы задаёт оболочка, а не сам бар: `page-padding` — тот же класс, что у `<main>`
            ниже, поэтому шапка и содержимое стоят на одной вертикали. Сверху `--sidebar-gap` —
            то же поле, что у карточки бокового меню до края окна. */}
        {!hideHeader && (
          <div className="flex-shrink-0 page-padding pt-[var(--sidebar-gap)]">
            <Header variant="card" />
          </div>
        )}

        {/* Горизонтальный flex под header */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Main content - растягивается, внутренний скролл.

              `relative` — скроллер обязан быть и содержащим блоком (владелец, 25.09.2026: «вся
              страница уехала вверх»). Скрытый `<select class="sr-only">` общего `Select` — это
              `absolute`, а позиционированного предка до этого места у него не было: содержащим
              блоком становился документ, элемент оставался на своей позиции глубоко внизу
              страницы, но уже ВНЕ этого скролла. Документ вырастал выше окна (2581 px при 720 на
              ревизии осмотра), и колесо, докрутив `<main>`, уводило вверх всю оболочку. Так —
              любой `absolute` без своего `relative`: он живёт внутри скролла и едет с контентом.
              Без `z-index` контекста наложения это не создаёт. */}
          <main
            id="main-content"
            tabIndex={-1}
            className={`
              relative flex-1 min-h-0 focus:outline-none
              ${
                fullHeight
                  ? // A viewport-locked screen still stands ON the canvas, not on top of it. Same
                    // horizontal field as every other page; vertically the rail's own gap rather
                    // than `page-padding-y`, because a conversation is measured in the viewport
                    // and 24 px twice over is a message and a half.
                    'overflow-hidden page-padding py-[var(--sidebar-gap)]'
                  : 'overflow-y-auto page-padding page-padding-y'
              }
              ${contentClassName || ''}
            `}
          >
            {children}
          </main>

          {/* Calculator panel (desktop only — it hides itself below 768 by its own class).
              Rendered on the viewport-locked messenger too since 2026-08-26: it returns `null`
              while closed, so it costs that route nothing, and without it the calculator button
              in the header was the one control on the bar that did nothing when pressed.

              Вертикаль карточки задаёт оболочка, как и у шапки: панель не знает, стоит ли она
              рядом с обычной страницей или с экраном, запертым в высоту окна, а поля у этих двух
              случаев разные — и её верх обязан совпасть с верхом содержимого. Правое поле панель
              держит сама (`page-padding-r`): оно одно на оба режима. */}
          <CalculatorPanel className={fullHeight ? 'py-[var(--sidebar-gap)]' : 'page-padding-y'} />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Mobile Layout
// ============================================================================

/**
 * Mobile layout with bottom navigation
 */
function MobileLayout({
  children,
  hideHeader,
  hideNavBar,
  contentClassName,
  fullHeight,
}: Omit<AppLayoutProps, 'hideSidebar'>): JSX.Element {
  const { isOpen: isMobileMenuOpen, close: closeMobileMenu } = useMobileMenu();

  // Lock body scroll when in fullHeight mode (prevents micro-scrolls from file pickers, etc.)
  useEffect(() => {
    if (!fullHeight) return;
    const html = document.documentElement;
    const body = document.body;
    html.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    body.style.position = 'fixed';
    body.style.inset = '0';
    body.style.width = '100%';
    return () => {
      html.style.overflow = '';
      html.style.overscrollBehavior = '';
      body.style.overflow = '';
      body.style.overscrollBehavior = '';
      body.style.position = '';
      body.style.inset = '';
      body.style.width = '';
    };
  }, [fullHeight]);

  return (
    <div
      // `data-mobile-layout` + `data-mobile-main`: a list page that scrolls inside its own table
      // (`PageContainer fill`) takes the window's height here — see «Телефон: страница-реестр берёт
      // высоту окна» in `index.css`. Everything else keeps growing with its content.
      data-mobile-layout=""
      className={`${fullHeight ? 'fixed inset-0 overflow-hidden overscroll-none pb-[env(safe-area-inset-bottom,0px)]' : 'min-h-dvh overflow-x-hidden'} bg-[var(--color-bg-app)] flex flex-col max-w-full`}
    >
      <SkipLink />
      {/* Content wrapper */}
      <div
        className={`flex flex-col flex-1 min-h-0 ${isMobileMenuOpen ? 'pointer-events-none' : ''}`}
        aria-hidden={isMobileMenuOpen}
      >
        {/* Header */}
        {!hideHeader && <Header />}

        {/* Content */}
        <main
          id="main-content"
          tabIndex={-1}
          // The bottom padding (room for the messenger button) moved to `index.css`: it was
          // `!pb-24`, a layered `!important` no unlayered rule can beat, and a list page that fills
          // the window needs a smaller one. `relative` — the same reason as on the desktop `<main>`:
          // a registry that fills the window scrolls in here, and an unanchored `absolute` would
          // otherwise sit outside that scroll and grow the document.
          data-mobile-main={fullHeight ? undefined : ''}
          className={`
            relative flex-1 min-h-0 focus:outline-none
            ${fullHeight ? 'overflow-hidden' : 'overflow-y-auto overflow-x-hidden page-padding page-padding-y'}
            w-full max-w-full
            ${contentClassName || ''}
          `}
        >
          {children}
        </main>
      </div>

      {/* Mobile drawer - рендерится ПОСЛЕДНИМ, z-[9999] гарантирует поверх всего */}
      <MobileDrawer isOpen={isMobileMenuOpen} onClose={closeMobileMenu} />
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Main application layout component
 *
 * Automatically switches between desktop and mobile layouts based on
 * the viewport width (550px breakpoint per TOSS design system).
 *
 * @example
 * ```tsx
 * // Basic usage — the page names itself with PageHeader, not the shell
 * <AppLayout>
 *   <ApplicationsList />
 * </AppLayout>
 *
 * // Full-screen mode (no sidebar/header)
 * <AppLayout
 *   hideSidebar
 *   hideHeader
 *   hideNavBar
 * >
 *   <FullScreenContent />
 * </AppLayout>
 * ```
 */
export function AppLayout({
  children,
  hideSidebar = false,
  hideHeader = false,
  hideNavBar = false,
  contentClassName = '',
  fullHeight = false,
}: AppLayoutProps): JSX.Element {
  const location = useLocation();
  const {
    showMobileNav,
    showSidebarCollapsed,
    showSidebarFull,
  } = useResponsive();
  const openCommandPalette = useUIStore((state) => state.openCommandPalette);

  // Global SignalR connection for messenger (real-time events everywhere)
  useMessengerSignalR();

  // State for manual sidebar collapse toggle (for desktop users who want collapsed view)
  // Persisted in localStorage to survive page reloads
  const [manualCollapse, setManualCollapse] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });

  // Handler for toggling sidebar collapse with localStorage persistence
  const handleToggleSidebar = () => {
    setManualCollapse((prev) => {
      const newValue = !prev;
      localStorage.setItem('sidebar-collapsed', String(newValue));
      return newValue;
    });
  };

  // Scroll to top on route change (skip for fullHeight/chat pages)
  useEffect(() => {
    if (!fullHeight) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, fullHeight]);

  // Global Cmd/Ctrl+K opens the command palette (layout-independent key code).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.code === 'KeyK') {
        e.preventDefault();
        openCommandPalette();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openCommandPalette]);

  // Record visited nav pages for the command palette "recent" section.
  useEffect(() => {
    if (NAV_LEAF_PATHS.has(location.pathname)) {
      pushRecentPath(location.pathname);
    }
  }, [location.pathname]);

  // Determine if sidebar should be collapsed
  // On tablet (768-1023px): always collapsed
  // On desktop (>= 1024px): collapsed if user manually toggled
  const shouldCollapseStidebar = showSidebarCollapsed || (showSidebarFull && manualCollapse);

  // Show sidebar (either collapsed or full) on tablet and desktop
  const showSidebar = showSidebarCollapsed || showSidebarFull;

  // Section roots of the internal app draw a history-only back arrow beside their title — the
  // desktop PWA window has no browser Back (owner, 23.09.2026). See `rootBackArrow.ts`.
  const content = <RootBackArrowContext.Provider value>{children}</RootBackArrowContext.Provider>;

  // Render mobile layout for mobile viewports (< 768px)
  if (showMobileNav) {
    return (
      <>
        <MobileLayout
          hideHeader={hideHeader}
          hideNavBar={hideNavBar}
          contentClassName={contentClassName}
          fullHeight={fullHeight}
        >
          {content}
          <MessengerFab />
        </MobileLayout>
        <CommandPalette />
      </>
    );
  }

  // Render tablet/desktop layout (>= 768px)
  return (
    <>
      <DesktopTabletLayout
        hideSidebar={hideSidebar || !showSidebar}
        hideHeader={hideHeader}
        contentClassName={contentClassName}
        fullHeight={fullHeight}
        sidebarCollapsed={shouldCollapseStidebar}
        onToggleSidebar={showSidebarFull ? handleToggleSidebar : undefined}
      >
        {content}
      </DesktopTabletLayout>
      <CommandPalette />
    </>
  );
}

// ============================================================================
// Additional Layout Components
// ============================================================================

/**
 * Layout wrapper for pages that need full-width content (no padding)
 */
export function FullWidthLayout({
  children,
  ...props
}: AppLayoutProps): JSX.Element {
  return <AppLayout {...props} contentClassName="p-0">{children}</AppLayout>;
}

/**
 * Layout wrapper for auth pages (no sidebar/navigation)
 */
export function AuthLayout({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="min-h-screen bg-[var(--color-bg-secondary)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {children}
      </div>
    </div>
  );
}

/**
 * Layout wrapper for error/empty pages
 */
export function CenteredLayout({ children }: { children: ReactNode }): JSX.Element {
  return (
    <AppLayout hideHeader hideNavBar hideSidebar>
      <div className="min-h-screen flex items-center justify-center p-4">
        {children}
      </div>
    </AppLayout>
  );
}

export default AppLayout;
